// Performance Drift Analysis - Time-based performance tracking and fatigue detection
import { FEATURE_FLAGS } from '../config/featureFlags.js';

// Analyze performance drift over time
export const analyzePerformanceDrift = (answers, timeSpent, questionSequence = []) => {
  if (!FEATURE_FLAGS.ADVANCED_SCORING) {
    return {
      consistencyTrend: 'stable',
      fatigueIndicators: [],
      improvementMetrics: {},
      summary: 'Performance analysis not available'
    };
  }

  const timeBasedMetrics = calculateTimeBasedMetrics(timeSpent, questionSequence);
  const consistencyAnalysis = analyzeConsistencyDrift(answers, questionSequence);
  const fatigueAnalysis = detectFatiguePatterns(timeSpent, answers, questionSequence);
  const improvementAnalysis = analyzeImprovement(answers, timeSpent, questionSequence);

  return {
    consistencyTrend: consistencyAnalysis.trend,
    consistencyChange: consistencyAnalysis.change,
    fatigueIndicators: fatigueAnalysis.indicators,
    fatigueScore: fatigueAnalysis.score,
    improvementMetrics: improvementAnalysis,
    timeMetrics: timeBasedMetrics,
    summary: generatePerformanceSummary(consistencyAnalysis, fatigueAnalysis, improvementAnalysis)
  };
};

// Calculate time-based performance metrics
const calculateTimeBasedMetrics = (timeSpent, questionSequence) => {
  const times = Object.entries(timeSpent)
    .filter(([questionId, time]) => time > 0)
    .sort(([a], [b]) => {
      const aIndex = questionSequence.indexOf(a);
      const bIndex = questionSequence.indexOf(b);
      return aIndex - bIndex;
    })
    .map(([, time]) => time);

  if (times.length < 3) {
    return { trend: 'insufficient_data', segments: [] };
  }

  // Divide into thirds for analysis
  const segmentSize = Math.floor(times.length / 3);
  const firstThird = times.slice(0, segmentSize);
  const middleThird = times.slice(segmentSize, segmentSize * 2);
  const finalThird = times.slice(segmentSize * 2);

  const segments = [
    {
      name: 'first_third',
      avgTime: calculateAverage(firstThird),
      variance: calculateVariance(firstThird),
      count: firstThird.length
    },
    {
      name: 'middle_third',
      avgTime: calculateAverage(middleThird),
      variance: calculateVariance(middleThird),
      count: middleThird.length
    },
    {
      name: 'final_third',
      avgTime: calculateAverage(finalThird),
      variance: calculateVariance(finalThird),
      count: finalThird.length
    }
  ];

  const trend = analyzeTrend(segments.map(s => s.avgTime));

  return {
    trend,
    segments,
    totalTime: times.reduce((sum, time) => sum + time, 0),
    avgTime: calculateAverage(times),
    timeVariance: calculateVariance(times)
  };
};

// Analyze consistency drift over time
const analyzeConsistencyDrift = (answers, questionSequence) => {
  const responses = Object.entries(answers)
    .filter(([questionId]) => questionSequence.includes(questionId))
    .sort(([a], [b]) => {
      const aIndex = questionSequence.indexOf(a);
      const bIndex = questionSequence.indexOf(b);
      return aIndex - bIndex;
    })
    .map(([, response]) => response);

  if (responses.length < 6) {
    return { trend: 'insufficient_data', change: 0 };
  }

  // Calculate consistency in segments
  const segmentSize = Math.floor(responses.length / 3);
  const firstHalf = responses.slice(0, Math.floor(responses.length / 2));
  const secondHalf = responses.slice(Math.floor(responses.length / 2));

  const firstConsistency = calculateSegmentConsistency(firstHalf);
  const secondConsistency = calculateSegmentConsistency(secondHalf);

  const change = secondConsistency - firstConsistency;
  const trend = change > 5 ? 'improving' : change < -5 ? 'declining' : 'stable';

  return {
    trend,
    change: Math.round(change),
    firstHalfConsistency: Math.round(firstConsistency),
    secondHalfConsistency: Math.round(secondConsistency)
  };
};

// Calculate consistency within a segment
const calculateSegmentConsistency = (responses) => {
  if (responses.length < 2) return 0;
  
  const variance = calculateVariance(responses);
  const consistency = Math.max(0, 100 - (variance * 25)); // Normalize to 0-100
  
  return consistency;
};

// Detect fatigue patterns
const detectFatiguePatterns = (timeSpent, answers, questionSequence) => {
  const indicators = [];
  let fatigueScore = 0;

  // Time-based fatigue indicators
  const timeMetrics = calculateTimeBasedMetrics(timeSpent, questionSequence);
  if (timeMetrics.segments.length >= 3) {
    const finalThirdTime = timeMetrics.segments[2].avgTime;
    const firstThirdTime = timeMetrics.segments[0].avgTime;
    
    // Significant increase in response time
    if (finalThirdTime > firstThirdTime * 1.5) {
      indicators.push('Response time increased significantly in final third');
      fatigueScore += 30;
    }
    
    // High variance in final third (inconsistent timing)
    if (timeMetrics.segments[2].variance > timeMetrics.segments[0].variance * 2) {
      indicators.push('Response timing became more erratic');
      fatigueScore += 20;
    }
  }

  // Response pattern fatigue indicators
  const responses = Object.entries(answers)
    .filter(([questionId]) => questionSequence.includes(questionId))
    .sort(([a], [b]) => questionSequence.indexOf(a) - questionSequence.indexOf(b))
    .map(([, response]) => response);

  if (responses.length >= 6) {
    const finalQuarter = responses.slice(-Math.floor(responses.length / 4));
    const neutralResponses = finalQuarter.filter(r => r === 3).length;
    
    // High neutral responses in final quarter
    if (neutralResponses / finalQuarter.length > 0.5) {
      indicators.push('Increased neutral responses toward the end');
      fatigueScore += 25;
    }
  }

  return {
    indicators,
    score: Math.min(100, fatigueScore),
    level: fatigueScore > 50 ? 'high' : fatigueScore > 25 ? 'moderate' : 'low'
  };
};

// Analyze improvement patterns
const analyzeImprovement = (answers, timeSpent, questionSequence) => {
  const consistencyAnalysis = analyzeConsistencyDrift(answers, questionSequence);
  const timeMetrics = calculateTimeBasedMetrics(timeSpent, questionSequence);
  
  const improvements = [];
  const declines = [];

  // Consistency improvement
  if (consistencyAnalysis.change > 10) {
    improvements.push({
      metric: 'consistency',
      change: consistencyAnalysis.change,
      description: `Consistency improved by ${consistencyAnalysis.change}%`
    });
  } else if (consistencyAnalysis.change < -10) {
    declines.push({
      metric: 'consistency',
      change: Math.abs(consistencyAnalysis.change),
      description: `Consistency declined by ${Math.abs(consistencyAnalysis.change)}%`
    });
  }

  // Response time optimization
  if (timeMetrics.segments.length >= 3) {
    const timeChange = ((timeMetrics.segments[2].avgTime - timeMetrics.segments[0].avgTime) / timeMetrics.segments[0].avgTime) * 100;
    
    if (timeChange < -15) {
      improvements.push({
        metric: 'response_time',
        change: Math.abs(Math.round(timeChange)),
        description: `Response time improved by ${Math.abs(Math.round(timeChange))}%`
      });
    }
  }

  return {
    improvements,
    declines,
    overallTrend: improvements.length > declines.length ? 'improving' : 
                  declines.length > improvements.length ? 'declining' : 'stable'
  };
};

// Analyze trend from array of values
const analyzeTrend = (values) => {
  if (values.length < 2) return 'insufficient_data';
  
  const firstValue = values[0];
  const lastValue = values[values.length - 1];
  const change = ((lastValue - firstValue) / firstValue) * 100;
  
  if (change > 15) return 'increasing';
  if (change < -15) return 'decreasing';
  return 'stable';
};

// Calculate average of array
const calculateAverage = (values) => {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
};

// Calculate variance of array
const calculateVariance = (values) => {
  if (values.length === 0) return 0;
  
  const mean = calculateAverage(values);
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  
  return calculateAverage(squaredDiffs);
};

// Generate performance summary
const generatePerformanceSummary = (consistency, fatigue, improvement) => {
  const summaryParts = [];
  
  // Consistency summary
  if (consistency.trend === 'improving') {
    summaryParts.push(`Your consistency improved by ${consistency.change}% during the assessment.`);
  } else if (consistency.trend === 'declining') {
    summaryParts.push(`Your consistency declined by ${Math.abs(consistency.change)}% during the assessment.`);
  } else {
    summaryParts.push('Your response consistency remained stable throughout the assessment.');
  }
  
  // Fatigue summary
  if (fatigue.level === 'high') {
    summaryParts.push('Signs of fatigue were detected in your response patterns.');
  } else if (fatigue.level === 'moderate') {
    summaryParts.push('Mild fatigue indicators were observed.');
  } else {
    summaryParts.push('No significant fatigue patterns were detected.');
  }
  
  // Improvement summary
  if (improvement.improvements.length > 0) {
    const primaryImprovement = improvement.improvements[0];
    summaryParts.push(primaryImprovement.description + ' during the final third of the assessment.');
  }
  
  return summaryParts.join(' ');
};

export default { analyzePerformanceDrift };